import { useCallback, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import {
  ChatActionBridgeContext,
  type ChatActionBridgeValue,
  type OpenChatRequest,
  type ReqGuardBridgeHandle,
  type WizardBridgeHandle,
} from './chatActionBridgeContext'

/**
 * Connects page features (the vECU test generator wizard, the Requirement Doctor page) to the global
 * floating Chat. Mounted once in RootLayout.
 */
export function ChatActionBridgeProvider({ children }: { children: ReactNode }) {
  const wizardRef = useRef<RefObject<WizardBridgeHandle> | null>(null)
  const [wizardAttached, setWizardAttached] = useState(false)
  const [wizardUseMock, setWizardUseMock] = useState(false)
  const reqGuardRef = useRef<RefObject<ReqGuardBridgeHandle> | null>(null)
  const [reqGuardAttached, setReqGuardAttached] = useState(false)
  const [reqGuardUseMock, setReqGuardUseMock] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [openRequest, setOpenRequest] = useState<OpenChatRequest | null>(null)

  const registerWizard = useCallback((handle: RefObject<WizardBridgeHandle>, useMock: boolean) => {
    wizardRef.current = handle
    setWizardAttached(true)
    setWizardUseMock(useMock)
    return () => {
      if (wizardRef.current === handle) {
        wizardRef.current = null
        setWizardAttached(false)
      }
    }
  }, [])

  const getWizard = useCallback(() => wizardRef.current?.current ?? null, [])

  const registerReqGuard = useCallback((handle: RefObject<ReqGuardBridgeHandle>, useMock: boolean) => {
    reqGuardRef.current = handle
    setReqGuardAttached(true)
    setReqGuardUseMock(useMock)
    return () => {
      if (reqGuardRef.current === handle) {
        reqGuardRef.current = null
        setReqGuardAttached(false)
      }
    }
  }, [])

  const getReqGuard = useCallback(() => reqGuardRef.current?.current ?? null, [])

  const requestOpenChat = useCallback((notice?: string) => {
    setOpenRequest((prev) => ({ seq: (prev?.seq ?? 0) + 1, notice }))
  }, [])

  const value = useMemo<ChatActionBridgeValue>(
    () => ({
      getWizard, wizardAttached, wizardUseMock, registerWizard,
      getReqGuard, reqGuardAttached, reqGuardUseMock, registerReqGuard,
      chatOpen, setChatOpen, openRequest, requestOpenChat,
    }),
    [
      getWizard, wizardAttached, wizardUseMock, registerWizard,
      getReqGuard, reqGuardAttached, reqGuardUseMock, registerReqGuard,
      chatOpen, openRequest, requestOpenChat,
    ],
  )

  return <ChatActionBridgeContext.Provider value={value}>{children}</ChatActionBridgeContext.Provider>
}
